import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Homepage from './pages/Homepage/Homepage';
import Contact from './pages/Contact/Contact';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import Portal from './pages/Portal/Portal';
import MLStudio from './pages/MLStudio/MLStudio';
import HelpCenter from './pages/HelpCenter/HelpCenter';
import Billing from './pages/Billing/Billing';
import About from './pages/About/About';
import PartnerWithUs from './pages/PartnerWithUs/PartnerWithUs';
import Privacy from './pages/Privacy/Privacy';
import Login from './pages/Login/Login';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import Signup from './pages/Signup/Signup';
import PasswordResetConfirm from './pages/PasswordResetConfirm/PasswordResetConfirm';
import EmailVerification from './pages/EmailVerification/EmailVerification';
import EmailVerificationPending from './pages/EmailVerificationPending/EmailVerificationPending';
import ResendVerification from './pages/ResendVerification/ResendVerification';
import ChangePassword from './pages/ChangePassword/ChangePassword';
import Account from './pages/Account/Account';
import ScopedInvitation from './pages/ScopedInvitation/ScopedInvitation';
import ProtectedRoute from './components/ProtectedRoute';
import FeatureGate from './components/FeatureGate';
import { AuthService, authManager } from '@df/api';
import { AuthReadyProvider, useAuthReady } from './contexts/AuthReadyContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import { CheckCircle } from 'lucide-react';
import { FaQuoteLeft, FaQuoteRight } from 'react-icons/fa6';
import { getConsent, loadGoogleAnalytics } from '@df/ui';

// Temporary placeholder components for new pages
const Services = () => (
  <div style={{ 
    padding: '3rem 2rem', 
    maxWidth: '1200px', 
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif'
  }}>
    {/* New Title and Description for Solutions Page */}
    <h1 style={{
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: '#2c5aa0',
      textAlign: 'center',
      marginBottom: '2rem'
    }}>
      All-in-One 3D/4D Data Solution - No More Stitching Tools Together!
    </h1>
    
    <div style={{
      display: 'flex',
      gap: '3rem',
      alignItems: 'flex-start',
      marginBottom: '3rem'
    }}>
      {/* Left side - Description */}
      <div style={{ flex: '1' }}>
        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: 'bold',
          color: '#2c5aa0',
          marginBottom: '1.5rem'
        }}>
          Why Datafre8?
        </h2>
        <ul style={{
          fontSize: '1.3rem',
          color: '#444',
          lineHeight: '1.6',
          paddingLeft: '0',
          listStyle: 'none'
        }}>
          <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <CheckCircle size={20} style={{ color: '#2c5aa0', marginTop: '2px', flexShrink: 0 }} />
            <span>Eliminates boilerplate coding to simplify complex 3D and 4D data workflows.</span>
          </li>
          <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <CheckCircle size={20} style={{ color: '#2c5aa0', marginTop: '2px', flexShrink: 0 }} />
            <span>Provides a unified, end-to-end solution for working with spatial and temporal data.</span>
          </li>
          <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <CheckCircle size={20} style={{ color: '#2c5aa0', marginTop: '2px', flexShrink: 0 }} />
            <span>Replaces fragmented tools with a streamlined, all-in-one platform.</span>
          </li>
          <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <CheckCircle size={20} style={{ color: '#2c5aa0', marginTop: '2px', flexShrink: 0 }} />
            <span>Accelerates and simplifies 3D machine learning, making it faster and more intuitive.</span>
          </li>
          <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <CheckCircle size={20} style={{ color: '#2c5aa0', marginTop: '2px', flexShrink: 0 }} />
            <span>Expands accessibility to advanced 3D ML for engineers, scientists, and organizations of all sizes.</span>
          </li>
        </ul>
      </div>
      
      {/* Vertical Divider */}
      <div style={{
        width: '1px',
        backgroundColor: '#e0e0e0',
        alignSelf: 'stretch',
        margin: '0 1rem'
      }}></div>
      
      {/* Right side - How you can use Datafre8 */}
      <div style={{ flex: '1' }}>
        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: 'bold',
          color: '#2c5aa0',
          marginBottom: '1.5rem'
        }}>
          Ways to Use Datafre8
        </h2>
        
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <img 
              src="/self-service.png" 
              alt="Self Service" 
              style={{
                width: '180px',
                height: '180px',
                objectFit: 'contain'
              }}
            />
            <div>
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#333',
                marginBottom: '0.5rem'
              }}>
                Self-Service
              </h3>
              <p style={{
                fontSize: '1.2rem',
                color: '#666',
                lineHeight: '1.5'
              }}>
                Access our platform directly to build, train, and deploy your 3D models with full control and flexibility.
              </p>
            </div>
          </div>
        </div>
        
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <img 
              src="/dfy.png" 
              alt="Done for You" 
              style={{
                width: '180px',
                height: '180px',
                objectFit: 'contain'
              }}
            />
            <div>
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#333',
                marginBottom: '0.5rem'
              }}>
                Done-for-You (DFY) Solution
              </h3>
              <p style={{
                fontSize: '1.2rem',
                color: '#666',
                lineHeight: '1.5'
              }}>
                Let our experts handle everything; from data preparation to model delivery—so you get results without the technical lift.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Core Services */}
    <div>
      <h2 style={{
        fontSize: '2.2rem',
        fontWeight: 'bold',
        color: '#2c5aa0',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        Explore What's Possible with Datafre8
      </h2>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '2rem'
      }}>
        {/* Service 1 */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <img 
              src="/sensor.png" 
              alt="Sensor Data Cleaning" 
              style={{
                width: '240px',
                height: '240px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: 'bold',
            color: '#2c5aa0',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Sensor Data Cleaning & Preparation
          </h3>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#666',
            margin: 0,
            textAlign: 'center'
          }}>
            Transform raw, noisy 3D sensor outputs into clean, structured data ready for analysis and model training.
          </p>
        </div>

        {/* Service 2 */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <img 
              src="/Prep.png" 
              alt="Data Processing & Annotation Tools" 
              style={{
                width: '240px',
                height: '240px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: 'bold',
            color: '#2c5aa0',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Data Processing & Annotation Tools
          </h3>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#666',
            margin: 0,
            textAlign: 'center'
          }}>
            Access tools and services for processing point clouds, simplifying meshes, and annotating complex 3D data at scale.
          </p>
        </div>

        {/* Service 3 */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <img 
              src="/Custom.png" 
              alt="Custom 3D ML Model Development" 
              style={{
                width: '240px',
                height: '240px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: 'bold',
            color: '#2c5aa0',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Custom 3D ML Model Development
          </h3>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#666',
            margin: 0,
            textAlign: 'center'
          }}>
            We design tailored machine learning models for your specific 3D data needs — from robotics and construction to logistics and beyond.
          </p>
        </div>

        {/* Service 4 */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <img 
              src="/deploy.png" 
              alt="Model Deployment & Integration" 
              style={{
                width: '240px',
                height: '240px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: 'bold',
            color: '#2c5aa0',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Model Deployment & Integration
          </h3>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#666',
            margin: 0,
            textAlign: 'center'
          }}>
            Seamlessly integrate trained models into your existing workflows, platforms, or applications with our expert support.
          </p>
        </div>

        {/* Service 5 */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <img 
              src="/cosulting.png" 
              alt="Consulting & Proof-of-Concepts" 
              style={{
                width: '240px',
                height: '240px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: 'bold',
            color: '#2c5aa0',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Consulting & Proof-of-Concepts
          </h3>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#666',
            margin: 0,
            textAlign: 'center'
          }}>
            Validate ideas quickly with expert-led PoC projects that test feasibility and de-risk larger investments.
          </p>
        </div>

        {/* Service 6 */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}>
          <div style={{
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <img 
              src="/api.png" 
              alt="API & SDK Integration" 
              style={{
                width: '240px',
                height: '240px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: 'bold',
            color: '#2c5aa0',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            API & SDK Integration
          </h3>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#666',
            margin: 0,
            textAlign: 'center'
          }}>
            Easily integrate our tools into your own systems using robust REST APIs and developer-friendly SDKs; extend, automate, and customize with ease.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const Ecosystem = () => (
  <div style={{ 
    padding: '3rem 2rem', 
    maxWidth: '1200px', 
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif'
  }}>
    {/* Page Title */}
    <h1 style={{
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: '#2c5aa0',
      textAlign: 'center',
      marginBottom: '2rem'
    }}>
      The Datafre8 Ecosystem
    </h1>

    {/* Hero Section with Image */}
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: '0 2rem',
      textAlign: 'center'
    }}>
      <p style={{
        fontSize: '1.2rem',
        lineHeight: '1.7',
        color: '#333',
        margin: '0 0 2rem 0',
        textAlign: 'center'
      }}>
        Our platform is built on a <strong>tightly integrated technology stack</strong>, purpose-built for low-code, scalable 3D computer vision. It is designed to eliminate the fragmentation and overhead that currently limit 3D computer vision adoption.
      </p>
      <img
        src="/ecosystem.png"
        alt="Ecosystem Hero"
        style={{
          width: '100%',
          maxWidth: '800px',
          height: 'auto',
          margin: '0 auto 2.5rem auto',
          display: 'block',
          borderRadius: '16px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        }}
      />
    </div>

    {/* Product Cards Section */}
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '2rem',
      justifyContent: 'center',
      margin: '2.5rem auto 0 auto',
      maxWidth: '1200px',
      padding: '0 2rem'
    }}>
      {/* Locomotiv Card */}
      <div style={{
        flex: '1 1 300px',
        minWidth: '280px',
        maxWidth: '370px',
        background: '#f8fafc',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        padding: '2rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginBottom: '1rem'
      }}>
        <h3 style={{
          fontSize: '1.8rem',
          fontWeight: 'bold',
          color: '#2c5aa0',
          marginBottom: '0.3rem'
        }}>
          Locomotiv
        </h3>
        <div style={{
          fontSize: '1.1rem',
          color: '#2c5aa0',
          fontWeight: 'bold',
          marginBottom: '0.5rem'
        }}>
          Python Library for Low Code 3D Computer Vision
        </div>
        <p style={{
          fontSize: '1.2rem',
          lineHeight: '1.7',
          color: '#333',
          margin: 0
        }}>
          At its core is Locomotiv, a Python-based low-code library that allows users to construct full 3D ML pipelines with far less code than traditional libraries. It abstracts away boilerplate code while maintaining the flexibility needed for advanced customization, making it equally powerful for ML engineers and domain specialists.
        </p>
      </div>
      {/* Lantern Card */}
      <div style={{
        flex: '1 1 300px',
        minWidth: '280px',
        maxWidth: '370px',
        background: '#f8fafc',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        padding: '2rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginBottom: '1rem'
      }}>
        <h3 style={{
          fontSize: '1.8rem',
          fontWeight: 'bold',
          color: '#2c5aa0',
          marginBottom: '0.3rem'
        }}>
          Lantern
        </h3>
        <div style={{
          fontSize: '1.1rem',
          color: '#2c5aa0',
          fontWeight: 'bold',
          marginBottom: '0.5rem'
        }}>
          Web-based 3D Data Visualization and Annotation
        </div>
        <p style={{
          fontSize: '1.2rem',
          lineHeight: '1.7',
          color: '#333',
          margin: 0
        }}>
          Complementing this is Lantern, a web-based interface for interactive 3D data visualization, annotation, and human-in-the-loop collaboration. It enables users to inspect data, label inputs, review predictions, and intervene at key points in the ML pipeline, all in a spatially intuitive format.
        </p>
      </div>
      {/* Railyard Card */}
      <div style={{
        flex: '1 1 300px',
        minWidth: '280px',
        maxWidth: '370px',
        background: '#f8fafc',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        padding: '2rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginBottom: '1rem'
      }}>
        <h3 style={{
          fontSize: '1.8rem',
          fontWeight: 'bold',
          color: '#2c5aa0',
          marginBottom: '0.3rem'
        }}>
          Railyard
        </h3>
        <div style={{
          fontSize: '1.1rem',
          color: '#2c5aa0',
          fontWeight: 'bold',
          marginBottom: '0.5rem'
        }}>
          3D Machine Learning in the Cloud
        </div>
        <p style={{
          fontSize: '1.2rem',
          lineHeight: '1.7',
          color: '#333',
          margin: 0
        }}>
          Finally, Railyard provides the cloud-native infrastructure that powers model training and deployment. It offers elastic compute and scalable storage optimized specifically for the heavy data and computational demands of 3D data, enabling easy transitions from development to production.
        </p>
      </div>
    </div>

    {/* Built for Flexibility Section */}
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '2.5rem 2rem',
      marginTop: '2.5rem'
    }}>
      <h2 style={{
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: '#2c5aa0',
        marginBottom: '1.5rem'
      }}>
        Built for Flexibility, Powered by Integration
      </h2>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '2rem'
      }}>
        <div>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            color: '#555',
            margin: '0 0 1.5rem 0'
          }}>
            Unlike general-purpose ML frameworks retrofitted for spatial data, Datafre8 is purpose-built for 3D. Every component—whether it's for data cleaning, point cloud processing, annotation, model training, or visualization—is optimized for the unique challenges of 3D geometry and spatial inference.
          </p>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            color: '#555',
            margin: 0
          }}>
            At the center is a powerful 3D AutoML engine that automates architecture design, tuning, and optimization, helping users achieve high-performance results without deep machine learning expertise.
          </p>
        </div>
        
        <div style={{
          backgroundColor: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ width: '100%', position: 'relative', minHeight: 80 }}>
            <FaQuoteLeft size={28} color="#2c5aa0" style={{ position: 'absolute', top: 0, left: 0 }} />
            <p style={{
              fontSize: '1.15rem',
              lineHeight: '1.6',
              color: '#2c5aa0',
              fontWeight: '500',
              margin: '0 2.2rem 0 2.2rem',
              fontStyle: 'italic',
              textAlign: 'left'
            }}>
              With Datafre8, domain experts can focus on innovation, not infrastructure - leveraging an ecosystem that replaces the need for 4 or 5 disconnected tools while remaining open, flexible, and scalable.
            </p>
            <FaQuoteRight size={28} color="#2c5aa0" style={{ position: 'absolute', bottom: 0, right: 0 }} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthReady } = useAuthReady();
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const rawLanternBase = process.env.REACT_APP_PUBLIC_LANTERN_V3_BASE || '';
  const lanternBase = rawLanternBase
    ? rawLanternBase.replace(/\/+$/, '')
    : (process.env.NODE_ENV === 'production' ? null : 'http://localhost:3000');

  // Define the routes where Navbar and Footer should not appear
  const noNavbarFooterRoutes = ['/portal', '/cloud-space', '/ml-studio', '/help-center', '/billing', '/login', '/forgot-password', '/signup', '/reset-password', '/reset-password-confirm', '/verify-email', '/verify-email-pending', '/resend-verification', '/change-password', '/me', '/scoped-invitation'];

  // Load Google Analytics if consent has been given (read-only, no banner)
  useEffect(() => {
    const consent = getConsent();
    if (consent === 'accepted') {
      loadGoogleAnalytics('G-7DP8Q69SYX');
    }
  }, []); // Run once on mount

  // Boot: hydrate tokens and verify session (no UI changes)
  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      const accessToken = authManager.getAccessToken();
      const refreshToken = authManager.getRefreshToken();

      try {
        if (!accessToken && refreshToken) {
          await authManager.refreshOnce();
        }
        if (authManager.getAccessToken()) {
          await AuthService.getCurrentUser();
          authManager.clearSuspect();
        }
      } catch (error) {
        const status = error?.status || error?.response?.status;
        if (status === 401) {
          console.warn('[AuthPortal] session verification failed (401) — clearing tokens');
          authManager.clearTokens();
        } else {
          const isTransport = error?.name === 'ApiTransportError' || error instanceof TypeError;
          authManager.markSuspect();
          const url = error?.url || 'unknown';
          const message = error?.message || 'unknown error';
          console.warn('[AuthPortal] session verification failed (transport)', {
            url,
            message,
            isTransport
          });
        }
      } finally {
        if (isMounted) {
          setIsValidatingToken(false);
          setAuthReady(true);
        }
      }
    };

    bootstrap();

    return () => {
      isMounted = false;
    };
  }, []); // Run once on mount only

  // Show loading screen while validating token
  if (isValidatingToken) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Validating authentication...
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <div>
      {/* Render Navbar only if the current route is not in the exclusion list */}
      {!noNavbarFooterRoutes.includes(location.pathname) && <Navbar />}
      
      <Routes>
        {/* Auth flows are route-based; avoid reintroducing legacy modal auth UIs without removing these pages. */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/solutions" element={<Services />} />
        <Route path="/ecosystem" element={<Ecosystem />} />
        <Route path="/portal" element={<ProtectedRoute><Portal /></ProtectedRoute>} />
        <Route
          path="/cloud-space"
          element={
            lanternBase
              ? <Navigate to={`${lanternBase}/datahub`} replace />
              : (
                <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
                  <Typography variant="body1">
                    Missing REACT_APP_PUBLIC_LANTERN_V3_BASE. Configure the Lantern base URL.
                  </Typography>
                </Box>
              )
          }
        />
        <Route path="/ml-studio" element={<ProtectedRoute><MLStudio /></ProtectedRoute>} />
        <Route path="/help-center" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
        <Route path="/contact-us" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/partner-with-us" element={<PartnerWithUs />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<PasswordResetConfirm />} />
        <Route path="/reset-password-confirm" element={<PasswordResetConfirm />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/verify-email-pending" element={<EmailVerificationPending />} />
        <Route path="/resend-verification" element={<ResendVerification />} />
        <Route path="/scoped-invitation" element={<ScopedInvitation />} />
        <Route path="/me" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      </Routes>

      {/* Render Footer only if the current route is not in the exclusion list */}
      {!noNavbarFooterRoutes.includes(location.pathname) && <Footer />}
    </div>
  );
}

// Wrap App with AuthReadyProvider
const AppWithAuthReady = () => (
  <AuthReadyProvider>
    <App />
  </AuthReadyProvider>
);

export default AppWithAuthReady;
